import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

interface DateRangeSliderProps {
  dateFrom?: string; // YYYY-MM format
  dateTo?: string; // YYYY-MM format
  minDate?: string; // YYYY-MM format
  maxDate?: string; // YYYY-MM format
  onChange: (dateFrom: string | undefined, dateTo: string | undefined) => void;
}

/**
 * Converts YYYY-MM to number of months since year 0
 */
function dateToMonthValue(date: string): number {
  const [year, month] = date.split('-').map(Number);
  return year * 12 + (month - 1);
}

/**
 * Converts month value to YYYY-MM format
 */
function monthValueToDate(value: number): string {
  const year = Math.floor(value / 12);
  const month = (value % 12) + 1;
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * Formats YYYY-MM to readable format (e.g., "2024. 01.")
 */
function formatDate(date: string): string {
  const [year, month] = date.split('-');
  return `${year}. ${month}.`;
}

export default function DateRangeSlider({
  dateFrom,
  dateTo,
  minDate,
  maxDate,
  onChange,
}: DateRangeSliderProps) {
  const { t } = useTranslation();
  // Default to min/max if not provided
  const effectiveMinDate = minDate || '2000-01';
  const effectiveMaxDate = maxDate || new Date().toISOString().slice(0, 7);

  const minValue = dateToMonthValue(effectiveMinDate);
  const maxValue = dateToMonthValue(effectiveMaxDate);

  // Initialize with min/max if not set
  const initialFrom = dateFrom || effectiveMinDate;
  const initialTo = dateTo || effectiveMaxDate;

  // Local state for slider values (only updates filter on commit)
  const [sliderValue, setSliderValue] = useState<[number, number]>([
    dateToMonthValue(initialFrom),
    dateToMonthValue(initialTo),
  ]);
  const latestValueRef = useRef<[number, number]>([dateToMonthValue(initialFrom), dateToMonthValue(initialTo)]);

  // Update local state when props change
  useEffect(() => {
    const newFrom = dateFrom ? dateToMonthValue(dateFrom) : minValue;
    const newTo = dateTo ? dateToMonthValue(dateTo) : maxValue;
    const newValue: [number, number] = [newFrom, newTo];
    setSliderValue(newValue);
    latestValueRef.current = newValue;
  }, [dateFrom, dateTo, minValue, maxValue]);

  // Track if user is currently dragging
  const isDraggingRef = useRef(false);

  // Handle slider value change (local update only, NO filter update)
  const handleSliderChange = useCallback((newValue: number[]) => {
    const value: [number, number] = [newValue[0], newValue[1]];
    setSliderValue(value);
    latestValueRef.current = value;
    isDraggingRef.current = true;
  }, []);

  // Handle filter update ONLY when mouse/touch is released
  useEffect(() => {
    const handlePointerUp = (e: PointerEvent) => {
      // Only commit if we were dragging
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        // Use a small timeout to ensure the slider value is fully updated
        setTimeout(() => {
          const [from, to] = latestValueRef.current;
          onChange(
            monthValueToDate(from),
            monthValueToDate(to)
          );
        }, 0);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        setTimeout(() => {
          const [from, to] = latestValueRef.current;
          onChange(
            monthValueToDate(from),
            monthValueToDate(to)
          );
        }, 0);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        setTimeout(() => {
          const [from, to] = latestValueRef.current;
          onChange(
            monthValueToDate(from),
            monthValueToDate(to)
          );
        }, 0);
      }
    };

    // Add multiple event listeners to catch all cases
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onChange]);

  const fromDateStr = monthValueToDate(sliderValue[0]);
  const toDateStr = monthValueToDate(sliderValue[1]);

  if (!minDate || !maxDate || minValue >= maxValue) {
    return <div className="text-sm text-muted-foreground">{t("dateRangeSlider.noDateRange")}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{t('images.installationDate')}</Label>
        <div className="text-xs text-muted-foreground">
          {formatDate(fromDateStr)} - {formatDate(toDateStr)}
        </div>
      </div>

      <Slider
        value={sliderValue}
        onValueChange={handleSliderChange}
        min={minValue}
        max={maxValue}
        step={1}
        className="w-full"
      />

      {/* Min/Max labels */}
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{formatDate(effectiveMinDate)}</span>
        <span>{formatDate(effectiveMaxDate)}</span>
      </div>
    </div>
  );
}
